import { useEffect } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';

interface UseKeyboardNavigationOptions {
  enableBackNavigation?: boolean;
  enableForwardNavigation?: boolean;
  enableEscapeToHome?: boolean;
}

export const useKeyboardNavigation = ({
  enableBackNavigation = true,
  enableForwardNavigation = true,
  enableEscapeToHome = false
}: UseKeyboardNavigationOptions = {}) => {
  const { goBack, goForward, navigationState } = useNavigation();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Alt + Left Arrow - Go back
      if (enableBackNavigation && event.altKey && event.key === 'ArrowLeft') {
        event.preventDefault();
        if (navigationState.canGoBack) {
          goBack();
        }
      }

      // Alt + Right Arrow - Go forward
      if (enableForwardNavigation && event.altKey && event.key === 'ArrowRight') {
        event.preventDefault();
        if (navigationState.canGoForward) {
          goForward();
        }
      }

      // Escape - Go to home (optional)
      if (enableEscapeToHome && event.key === 'Escape') {
        event.preventDefault();
        window.location.href = '/';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goBack, goForward, navigationState, enableBackNavigation, enableForwardNavigation, enableEscapeToHome]);

  return {
    canGoBack: navigationState.canGoBack,
    canGoForward: navigationState.canGoForward,
    navigationDepth: navigationState.depth
  };
};